import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { NumberUtils, CONS, } from '@pomofocus/core';
import { globalActions } from '../../store/slices/globalSlice';
import { getApiClient } from '../../utils/apiClient';
import { timerActions } from './timerSlice';
// Async thunks for API operations
const fetchActivities = createAsyncThunk('activity/fetchActivities', async () => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todos',
        method: 'get',
    });
    return response.data;
});
const apiCreateTodo = createAsyncThunk('activity/apiCreateTodo', async (data) => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todo',
        method: 'post',
        data: data,
    });
    return { ...data, ...response.data };
});
const apiIncrementActPomodoro = createAsyncThunk('activity/apiIncrementActPomodoro', async (data) => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todo/increment-act-pomodoro',
        method: 'post',
        data: {
            todoId: data.todoId,
            estPomodoroUnit: data.estPomodoroUnit,
        },
    });
    return data;
});
const apiDeleteTodo = createAsyncThunk('activity/apiDeleteTodo', async (todoId) => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todo/delete',
        method: 'post',
        data: { todoId },
    });
    return todoId;
});
const apiGetAllTodo = createAsyncThunk('activity/apiGetAllTodo', async () => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todos',
        method: 'get',
    });
    return response.data;
});
const apiClearActPomodoros = createAsyncThunk('activity/apiClearActPomodoros', async () => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todo/clear-act-pomodoros',
        method: 'post',
    });
    return {};
});
const apiClearFinishedTasks = createAsyncThunk('activity/apiClearFinishedTasks', async () => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todo/clear-finished-tasks',
        method: 'post',
    });
    return {};
});
const apiClearAllTasks = createAsyncThunk('activity/apiClearAllTasks', async () => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todo/clear-all-tasks',
        method: 'post',
    });
    return {};
});
const apiCheckTask = createAsyncThunk('activity/apiCheckTask', async (data) => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todo/check-task',
        method: 'post',
        data: data,
    });
    return data;
});
const apiUpdateTodo = createAsyncThunk('activity/apiUpdateTodo', async (data) => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todo/update',
        method: 'post',
        data: data,
    });
    return data;
});
const apiUpdateTodos = createAsyncThunk('activity/apiUpdateTodos', async (todos) => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/todos/update',
        method: 'post',
        data: { todos },
    });
    return todos;
});
const apiGetTodoTemplate = createAsyncThunk('activity/apiGetTodoTemplate', async () => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todo-template',
        method: 'get',
    });
    return response.data;
});
const saveTemplate = createAsyncThunk('activity/saveTemplate', async (todoTemplate) => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todo-template',
        method: 'post',
        data: { todoTemplate },
    });
    return response.data;
});
const updateTemplate = createAsyncThunk('activity/updateTemplate', async (data) => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todo-template-update',
        method: 'post',
        data: data,
    });
    return response.data;
});
const apiUpdateTodoTemplate = createAsyncThunk('activity/apiUpdateTodoTemplate', async (data, { dispatch }) => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todo-template-update',
        method: 'post',
        data: {
            templateId: data.templateId,
            templateName: data.templateName,
            todos: data.todos,
        },
    });
    dispatch(globalActions.changeVisibleModal({
        visibleModal: CONS.MODAL_TEMPLATE_SELECTION,
    }));
    return response.data;
});
const removeTemplate = createAsyncThunk('activity/removeTemplate', async (templateId) => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/remove-todo-template',
        method: 'post',
        data: { templateId },
    });
    return templateId;
});
const stashTasks = createAsyncThunk('activity/stashTasks', async (activityIds) => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/stash-tasks',
        method: 'post',
        data: { activityIds },
    });
    return activityIds;
});
const unstashTasks = createAsyncThunk('activity/unstashTasks', async () => {
    const apiClient = getApiClient();
    await apiClient({
        url: '/api/unstash-tasks',
        method: 'post',
    });
    return {};
});
const apiSaveTodoTemplate = createAsyncThunk('activity/apiSaveTodoTemplate', async (todoTemplate, { dispatch }) => {
    const apiClient = getApiClient();
    const response = await apiClient({
        url: '/api/todo-template',
        method: 'post',
        data: { todoTemplate },
    });
    dispatch(globalActions.changeVisibleModal({
        visibleModal: CONS.MODAL_TEMPLATE_SELECTION,
    }));
    return response.data;
});
// Helper function to handle individual activity updates
const updateActivityItem = (activity, id, updates) => {
    if (activity.id !== id)
        return activity;
    return { ...activity, ...updates };
};
// Helper function to process activity items (similar to original activityItem function)
const processActivityItem = (activity, action) => {
    switch (action.type) {
        case 'activity/incrementActPomodoro':
            if (activity.id !== action.payload.id)
                return activity;
            return {
                ...activity,
                actPomodoro: NumberUtils.eliminateFloatNumber(activity.actPomodoro + action.payload.estPomodoroUnit),
            };
        case 'activity/checkTask':
            if (activity.id !== action.payload.id)
                return activity;
            return { ...activity, done: action.payload.newDone };
        case 'activity/getAllActivitiesOk':
            return {
                ...activity,
                id: activity.id,
                projectName: activity.projectName,
                title: activity.title,
                actPomodoro: activity.actPomodoro,
                estPomodoro: activity.estPomodoro,
                done: activity.done === 1 || activity.done === true,
                apiTaskId: activity.apiTaskId || '',
                stashed: activity.stashed || false,
            };
        default:
            return activity;
    }
};
// Initial state
const initialState = {
    activities: [],
    template: [],
    selectedId: undefined,
    editActivityId: undefined,
    callCheckActiveTaskTS: 0,
    templateIdToEdit: '',
};
// Activity slice
const activitySlice = createSlice({
    name: 'activity',
    initialState,
    reducers: {
        // Select activity
        selectActivity: (state, action) => {
            state.selectedId = action.payload.id;
        },
        // Save/update activity
        saveActivity: (state, action) => {
            const activityIndex = state.activities.findIndex((a) => a.id === action.payload.id);
            if (activityIndex !== -1) {
                state.activities[activityIndex] = {
                    ...state.activities[activityIndex],
                    title: action.payload.title,
                    estPomodoro: action.payload.estPomodoro,
                    actPomodoro: action.payload.actPomodoro,
                    note: action.payload.note,
                    projectName: action.payload.projectName,
                    done: action.payload.done,
                };
            }
        },
        // Replace all activities
        saveActivities: (state, action) => {
            state.activities = action.payload.todos;
        },
        // Delete activity
        deleteActivity: (state, action) => {
            state.activities = state.activities.filter((a) => a.id !== action.payload.id);
        },
        // Create new activity
        createActivity: (state, action) => {
            state.activities.push({
                id: action.payload.id,
                projectName: action.payload.projectName,
                title: action.payload.title,
                actPomodoro: action.payload.actPomodoro,
                estPomodoro: action.payload.estPomodoro,
                note: action.payload.note,
                order: action.payload.order,
                apiTaskId: action.payload.apiTaskId,
                done: action.payload.done || false,
            });
        },
        // Increment actual pomodoro count
        incrementActPomodoro: (state, action) => {
            const activityIndex = state.activities.findIndex((a) => a.id === action.payload.id);
            if (activityIndex !== -1) {
                state.activities[activityIndex].actPomodoro =
                    NumberUtils.eliminateFloatNumber(state.activities[activityIndex].actPomodoro +
                        action.payload.estPomodoroUnit);
            }
        },
        // Clear finished tasks
        clearFinishedTasks: (state) => {
            state.activities = state.activities.filter((a) => !a.done || a.stashed);
        },
        // Clear all tasks (keep stashed)
        clearAllTasks: (state) => {
            state.activities = state.activities.filter((a) => a.stashed);
        },
        // Clear actual pomodoro counts
        clearActPomodoros: (state) => {
            state.activities = state.activities.map((a) => {
                if (a.stashed)
                    return a;
                return { ...a, actPomodoro: 0 };
            });
        },
        // Check/uncheck task
        checkTask: (state, action) => {
            const activityIndex = state.activities.findIndex((a) => a.id === action.payload.id);
            if (activityIndex !== -1) {
                state.activities[activityIndex].done = action.payload.newDone;
            }
        },
        // Overwrite activities (preserve stashed)
        overWriteActivities: (state, action) => {
            const stashedActivities = state.activities.filter((a) => a.stashed);
            state.activities = [
                ...action.payload.activities,
                ...stashedActivities,
            ];
        },
        // Update todo templates
        updateTodoTemplate: (state, action) => {
            state.template = action.payload.todoTemplate;
        },
        // Call switch to next task
        callSwitchToNextTask: (state, action) => {
            state.callCheckActiveTaskTS = action.payload.now.getTime();
        },
        // Set template ID to edit
        setTemplateIdToEdit: (state, action) => {
            state.templateIdToEdit = action.payload.templateId;
        },
        // Set edit activity ID
        setEditActivityId: (state, action) => {
            state.editActivityId = action.payload.editActivityId;
        },
    },
    extraReducers: (builder) => {
        // Handle create todo
        builder.addCase(apiCreateTodo.fulfilled, (state, action) => {
            // Activity already added by optimistic update, just update with any server data
            const activityIndex = state.activities.findIndex((a) => a.id === action.payload.id);
            if (activityIndex !== -1) {
                state.activities[activityIndex] = {
                    ...state.activities[activityIndex],
                    ...action.payload,
                };
            }
        });
        builder.addCase(apiCreateTodo.pending, (state, action) => {
            // Optimistic update - add activity immediately
            const data = action.meta.arg;
            state.activities.push({
                id: data.id,
                projectName: data.projectName,
                title: data.title,
                actPomodoro: data.actPomodoro,
                estPomodoro: data.estPomodoro,
                note: data.note,
                order: data.order,
                apiTaskId: data.apiTaskId,
                done: false,
            });
        });
        builder.addCase(apiCreateTodo.rejected, (state, action) => {
            // Remove optimistically added activity on failure
            const data = action.meta.arg;
            state.activities = state.activities.filter((a) => a.id !== data.id);
        });
        // Handle increment pomodoro
        builder.addCase(apiIncrementActPomodoro.pending, (state, action) => {
            // Optimistic update
            const { todoId, estPomodoroUnit } = action.meta.arg;
            const activityIndex = state.activities.findIndex((a) => a.id === todoId);
            if (activityIndex !== -1) {
                state.activities[activityIndex].actPomodoro =
                    NumberUtils.eliminateFloatNumber(state.activities[activityIndex].actPomodoro +
                        estPomodoroUnit);
            }
        });
        // Handle delete todo
        builder.addCase(apiDeleteTodo.pending, (state, action) => {
            // Optimistic update - remove immediately
            const todoId = action.meta.arg;
            state.activities = state.activities.filter((a) => a.id !== todoId);
        });
        builder.addCase(apiDeleteTodo.rejected, (state, action) => {
            // Would need to restore the activity here if we had kept it
            // For now, we'll rely on a full refresh
        });
        // Handle get all todos
        builder.addCase(apiGetAllTodo.fulfilled, (state, action) => {
            const sortedActivities = [...action.payload].sort((a, b) => a.order - b.order);
            state.activities = sortedActivities.map((activity) => ({
                ...activity,
                done: activity.done === 1 || activity.done === true,
                apiTaskId: activity.apiTaskId || '',
                stashed: activity.stashed || false,
            }));
        });
        // Handle clear operations
        builder.addCase(apiClearActPomodoros.pending, (state) => {
            // Optimistic update
            state.activities = state.activities.map((a) => {
                if (a.stashed)
                    return a;
                return { ...a, actPomodoro: 0 };
            });
        });
        builder.addCase(apiClearFinishedTasks.pending, (state) => {
            // Optimistic update
            state.activities = state.activities.filter((a) => !a.done || a.stashed);
        });
        builder.addCase(apiClearAllTasks.pending, (state) => {
            // Optimistic update
            state.activities = state.activities.filter((a) => a.stashed);
        });
        // Handle check task
        builder.addCase(apiCheckTask.pending, (state, action) => {
            // Optimistic update
            const { id, newDone } = action.meta.arg;
            const activityIndex = state.activities.findIndex((a) => a.id === id);
            if (activityIndex !== -1) {
                state.activities[activityIndex].done = newDone;
            }
        });
        // Handle update todo
        builder.addCase(apiUpdateTodo.pending, (state, action) => {
            // Optimistic update
            const data = action.meta.arg;
            const activityIndex = state.activities.findIndex((a) => a.id === data.id);
            if (activityIndex !== -1) {
                state.activities[activityIndex] = {
                    ...state.activities[activityIndex],
                    ...data,
                };
            }
        });
        // Handle update todos
        builder.addCase(apiUpdateTodos.pending, (state, action) => {
            // Optimistic update
            state.activities = state.activities.map((a) => {
                const found = action.meta.arg.find((na) => na.id === a.id);
                return found || a;
            });
        });
        // Handle template operations
        builder.addCase(apiGetTodoTemplate.fulfilled, (state, action) => {
            state.template = action.payload;
        });
        builder.addCase(saveTemplate.fulfilled, (state, action) => {
            state.template.push(action.payload);
        });
        builder.addCase(updateTemplate.fulfilled, (state, action) => {
            const templateIndex = state.template.findIndex((t) => t._id === action.payload._id);
            if (templateIndex !== -1) {
                state.template[templateIndex] = action.payload;
            }
        });
        builder.addCase(apiUpdateTodoTemplate.fulfilled, (state, action) => {
            const templateIndex = state.template.findIndex((t) => t._id === action.payload._id);
            if (templateIndex !== -1) {
                state.template[templateIndex] = action.payload;
            }
        });
        builder.addCase(removeTemplate.fulfilled, (state, action) => {
            state.template = state.template.filter((t) => t._id !== action.payload);
        });
        // Handle stash operations
        builder.addCase(stashTasks.pending, (state, action) => {
            const taskIds = action.meta.arg;
            const hidingTasks = state.activities.filter((a) => taskIds.includes(a.id));
            const hiddenTasks = state.activities.filter((a) => a.stashed);
            const newHiddenTasks = [...hiddenTasks, ...hidingTasks];
            newHiddenTasks.forEach((task, index) => {
                task.order = index;
                task.stashed = true;
            });
            const newVisibleTasks = state.activities.filter((a) => !taskIds.includes(a.id) && !a.stashed);
            state.activities = [...newVisibleTasks, ...newHiddenTasks];
        });
        builder.addCase(unstashTasks.pending, (state) => {
            const visibleTasks = state.activities.filter((a) => !a.stashed);
            const stashedTasks = state.activities.filter((a) => a.stashed);
            visibleTasks.sort((a, b) => a.order - b.order);
            stashedTasks.sort((a, b) => a.order - b.order);
            const newActivities = [...visibleTasks, ...stashedTasks];
            newActivities.forEach((a, i) => {
                a.order = i;
                a.stashed = false;
            });
            state.activities = newActivities;
        });
        // Handle fetch activities
        builder.addCase(fetchActivities.fulfilled, (state, action) => {
            state.activities = action.payload;
        });
        builder.addCase(timerActions.loadTimerState.fulfilled, (state, action) => {
            // fixed
            state.selectedId = action.payload.timerState.taskId;
        });
        builder.addCase(apiSaveTodoTemplate.fulfilled, (state, action) => {
            state.template = [...state.template, action.payload.template];
        });
    },
});
// Selectors
export const selectActivityState = (state) => state.activity;
export const selectActivities = (state) => state.activity.activities;
export const selectSelectedActivity = (state) => {
    const activities = state.activity.activities;
    const selectedId = state.activity.selectedId;
    return activities.find((a) => a.id === selectedId);
};
export const selectSelectedActivityId = (state) => state.activity.selectedId;
export const selectEditActivityId = (state) => state.activity.editActivityId;
export const selectTodoTemplates = (state) => state.activity.template;
export const selectCallCheckActiveTaskTS = (state) => state.activity.callCheckActiveTaskTS;
export const selectTemplateIdToEdit = (state) => state.activity.templateIdToEdit;
export const selectVisibleActivities = (state) => state.activity.activities.filter((a) => !a.stashed);
export const selectStashedActivities = (state) => state.activity.activities.filter((a) => a.stashed);
export const selectUnfinishedActivities = (state) => state.activity.activities.filter((a) => !a.done);
export const selectFinishedActivities = (state) => state.activity.activities.filter((a) => a.done);
export const activityActions = {
    ...activitySlice.actions,
    // async actions
    fetchActivities,
    apiCreateTodo,
    apiIncrementActPomodoro,
    apiDeleteTodo,
    apiGetAllTodo,
    apiClearActPomodoros,
    apiClearFinishedTasks,
    apiClearAllTasks,
    apiCheckTask,
    apiUpdateTodo,
    apiUpdateTodos,
    apiGetTodoTemplate,
    saveTemplate,
    updateTemplate,
    apiUpdateTodoTemplate,
    removeTemplate,
    stashTasks,
    unstashTasks,
    apiSaveTodoTemplate,
};
export const activitySelectors = {
    selectActivityState,
    selectActivities,
    selectSelectedActivity,
    selectSelectedActivityId,
    selectEditActivityId,
    selectTodoTemplates,
    selectCallCheckActiveTaskTS,
    selectTemplateIdToEdit,
    selectVisibleActivities,
    selectStashedActivities,
    selectUnfinishedActivities,
    selectFinishedActivities,
};
// Export reducer
export default activitySlice.reducer;
